import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { stagePrice, unstagePrice, type GridRow, type PriceRunLine, type StageBody } from "./api";
import { invalidateBatch } from "./batch";
import { COPY, money } from "./format";
import { cleanPrice, needsDrawer, stageRefusal, stageTarget, stepPrice, type Refusal } from "./price";
import { PREVIEW_COPY, type RowPreview } from "./row_preview";

/** The words of the grid's own price edit (C1 v1.57, go-live to-do T25). */
export const GRID_EDIT_COPY = {
  field: (product: string) => `New price for ${product}`,
  reset: "Reset",
  savedWord: "Saved",
  live: (price: string | null) => `Live ${price ?? "none yet"}`,
  removeShort: "Remove",
} as const;

/** The stage body a cell's Save sends: the drawer's, the row's target and the price, no reason, exception or temporary price. */
export function gridSaveBody(row: GridRow, price: string): StageBody {
  return { ...stageTarget(row), new_price: price };
}
/** What a cell's Save does with the typed price: ask again, open the drawer (the saved line holds a field the cell does not), or stage. */
export function gridSaveAction(value: string, saved: PriceRunLine | null): { kind: "enter" } | { kind: "drawer" } | { kind: "stage"; price: string } {
  const price = cleanPrice(value);
  if (!price) return { kind: "enter" };
  if (needsDrawer(saved)) return { kind: "drawer" };
  return { kind: "stage", price };
}
/** A refusal that asks for a field the cell cannot hold finishes in the drawer (GATE-1084 PS-2 A); any other is said in the cell. */
export function finishesInDrawer(r: Refusal): boolean {
  return r.needsReason || Boolean(r.needsException);
}

/** One row being edited in the grid: its key and the price as typed. One row at a time; editing another resets the first. */
export type GridEdit = { key: string; value: string };
/**
 * The edit left once a row's cell is done (bug 4020402f): Reset (no price) ends the row's edit; a Save ends it only while the field
 * still holds the price sent, so a price typed while the Save was in flight stays in the field to be saved in turn.
 */
export function afterGridSave(edit: GridEdit | null, key: string, sent?: string): GridEdit | null {
  if (edit?.key !== key) return edit;
  return sent === undefined || cleanPrice(edit.value) === sent ? null : edit;
}

/**
 * The in-cell stepper (UX v0.27 R1-3, PriceStepper): minus and plus around the price, stepping by the currency's step (GATE-1083
 * PS-1 A). Typing or stepping puts this row in edit. Only the row in edit asks C2 for a preview, the drawer's projection call
 * unchanged, once the typed price has rested; the line under the field names the price shown as not saved. Nothing here computes
 * a margin (H12). A click in the cell never opens the drawer or toggles a store row.
 */
export function GridStepper({ row, value, editing, currency, preview, onChange }: {
  row: GridRow; value: string; editing: boolean; currency: string | null; preview: RowPreview;
  onChange: (value: string) => void;
}) {
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <Button type="button" size="sm" variant="outline" className="size-7 p-0" aria-label={COPY.lower} onClick={() => onChange(stepPrice(value, -1, currency))}><Minus aria-hidden className="size-3" /></Button>
        <Input aria-label={GRID_EDIT_COPY.field(row.product)} inputMode="decimal" className="h-7 w-20 px-1 font-bold tabular-nums" value={value}
          data-grid-price onChange={(e) => onChange(e.target.value)} />
        <Button type="button" size="sm" variant="outline" className="size-7 p-0" aria-label={COPY.raise} onClick={() => onChange(stepPrice(value, 1, currency))}><Plus aria-hidden className="size-3" /></Button>
      </div>
      {editing && preview.kind === "none" && <p className="mt-1 text-xs text-ks-grey-1">{COPY.projEnter}</p>}
      {editing && preview.kind === "pending" && <p role="status" className="mt-1 text-xs text-ks-grey-1">{COPY.projPending(money(preview.typed, currency))}</p>}
      {editing && preview.kind === "refused" && <p role="alert" className="mt-1 text-xs">{preview.text}</p>}
      {editing && preview.kind === "answer" && <p className="mt-1 text-xs text-ks-ink" data-grid-preview>{PREVIEW_COPY.notSaved(money(preview.p.price.value, preview.p.currency))}</p>}
    </div>
  );
}

/**
 * The Save column. A row in edit: Save and Reset. Save stages the typed price through C2's stage route with the drawer's body; a
 * refusal that needs fields the cell cannot hold (a floor reason, an RRP exception) opens the drawer on the row with the typed
 * price and the refusal (GATE-1084 PS-2 A), and so does a saved line carrying such a field or a temporary price, so a re-save
 * never drops it. Any other refusal is said under the button. A row with a saved line and no edit: Saved and Remove. In a partner
 * view Save is off (C2 v1.23 ruling A5) and Remove stays, exactly as in the drawer's New price box: Remove takes a saved LIST
 * price out of the batch, never a partner price, so the ruling does not cover it. The cell never judges a floor or an RRP itself.
 */
export function GridSaveCell({ row, saved, editing, value, currency, partner, areaName, onDone, onCarry }: {
  row: GridRow; saved: PriceRunLine | null; editing: boolean; value: string; currency: string | null; partner: boolean; areaName: string;
  onDone: (sent?: string) => void; onCarry: (value: string, refusal: Refusal | null) => void;
}) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Editing another row resets this one, its refusal line with it (one row in edit at a time).
  const [wasEditing, setWasEditing] = useState(editing);
  if (wasEditing !== editing) { setWasEditing(editing); if (!editing) setError(null); }
  const stage = useMutation({
    mutationFn: (price: string) => stagePrice(gridSaveBody(row, price)),
    onSuccess: (_, price) => { invalidateBatch(client); setError(null); onDone(price); },
    onError: (e, price) => {
      const r = stageRefusal(e, areaName, currency, money(price, currency));
      if (finishesInDrawer(r)) { setError(null); onCarry(price, r); return; } // the price sent, which the refusal is about
      setError(r.text);
    },
  });
  const remove = useMutation({
    mutationFn: (lineId: number) => unstagePrice(lineId),
    onSuccess: () => { invalidateBatch(client); setError(null); },
    onError: (e) => setError(stageRefusal(e, areaName, currency, "").text),
  });
  const save = () => {
    const action = gridSaveAction(value, saved);
    if (action.kind === "enter") setError(COPY.enterPrice);
    else if (action.kind === "drawer") onCarry(value, null);
    else stage.mutate(action.price);
  };
  const busy = stage.isPending || remove.isPending;
  return (
    <div className="flex flex-col items-start gap-1" onClick={(e) => e.stopPropagation()}>
      {editing ? (
        <>
          {/* Save and Remove carry no `data-nonmutating`, so for a view-only person the shell renders them inert (H-3, AGENTS rule 11)
              and C2 refuses the write, as with the drawer's Save. */}
          <Button type="button" size="sm" disabled={busy || partner} aria-busy={stage.isPending} data-grid-save onClick={save}>
            {stage.isPending ? <><Loader2 aria-hidden className="size-4 animate-spin" />{COPY.saving}</> : COPY.save}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busy} data-nonmutating onClick={() => { setError(null); onDone(); }}>{GRID_EDIT_COPY.reset}</Button>
        </>
      ) : saved ? (
        <>
          <Badge variant="outline" className="border-ks-warn text-ks-warn" data-grid-saved>{GRID_EDIT_COPY.savedWord}</Badge>
          <Button type="button" size="sm" variant="ghost" disabled={busy} title={COPY.remove} onClick={() => remove.mutate(saved.id)}>{GRID_EDIT_COPY.removeShort}</Button>
        </>
      ) : null}
      {/* AGENTS.md rule 14: a refusal is an alert. */}
      {error && <p role="alert" className="text-xs">{error}</p>}
    </div>
  );
}
